/**
 * Build Section Execution Utilities
 * 
 * Core execution logic for building slide sections.
 * 
 * ARCHITECTURE: This module orchestrates existing tools (DRY principle).
 * - createShape → uses CreateOrEditShapeTool (Office.js + COM fallback)
 * - createTable → uses CreateOrEditTableTool (Office.js + COM fallback)
 * - createChart → uses CreateOrEditChartTool (Office.js + COM fallback)
 * - createConnector → uses CreateOrEditConnectorTool (Office.js + COM fallback)
 * - copy → uses CopyShapesTool (COM only - Office.js doesn't support shape copying)
 * 
 * Schema conversion: buildSection uses paragraphs[], tools use paragraphOperations[]
 * The converters wrap paragraphs in "insert" operations for compatibility.
 * 
 * Used by both:
 * - BuildSectionTool (for agent tool calls)
 * - Direct execution (for programmatic use)
 */

import { getOrCreatedocumentId } from '../../../../services/documentIdManager';
import { getContextResolver } from '../../../core/contextResolver';
import { applyShapeMetadata } from '../../../../services/metadataService';
import type { CollectionItem, BuildSectionParams, TableConfig, ChartConfig } from './liveBuildArea.schema';

// Import existing tools for DRY execution
import { CreateOrEditShapeTool, type CreateOrEditShapeResult } from '../../liveEditor/tools/createOrEditShape/createOrEditShape.tool';
import { CreateOrEditTableTool, type CreateOrEditTableResult } from '../../liveEditor/tools/createOrEditTable/createOrEditTable.tool';
import { CreateOrEditChartTool, type CreateOrEditChartResult } from '../../liveEditor/tools/createOrEditChart/createOrEditChart.tool';
import { CreateOrEditConnectorTool, type CreateOrEditConnectorResult } from '../../liveEditor/tools/createOrEditConnector/createOrEditConnector.tool';
import { CopyShapesTool, type CopyShapesResult } from '../../liveEditor/tools/copyShapes/copyShapes.tool';

// Import types for schema conversion
import type { CreateOrEditShapeParams } from '../../liveEditor/tools/createOrEditShape/createOrEditShape.schema';
import type { CreateOrEditTableParams } from '../../liveEditor/tools/createOrEditTable/createOrEditTable.schema';
import type { CreateOrEditChartParams } from '../../liveEditor/tools/createOrEditChart/createOrEditChart.schema';
import type { CreateOrEditConnectorParams } from '../../liveEditor/tools/createOrEditConnector/createOrEditConnector.schema';
import type { CopyShapesParams } from '../../liveEditor/tools/copyShapes/copyShapes.schema';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface BuildSectionExecutionContext {
    documentId?: string;
    slideId: number;
    targetArea: {
        left: number;
        top: number;
        width: number;
        height: number;
    };
}

export interface CreatedShapeInfo {
    type: string;
    shapeId: number;
    position: {
        left: number;
        top: number;
        width: number;
        height: number;
    };
    warnings: string[];
}

export interface ExecutionResult {
    success: boolean;
    createdShapes: CreatedShapeInfo[];
    warnings: string[];
    errors: string[];
}

// ═══════════════════════════════════════════════════════════════════════════
// TOOL INSTANCES (reused for all operations)
// ═══════════════════════════════════════════════════════════════════════════

const createOrEditShapeTool = new CreateOrEditShapeTool();
const createOrEditTableTool = new CreateOrEditTableTool();
const createOrEditChartTool = new CreateOrEditChartTool();
const createOrEditConnectorTool = new CreateOrEditConnectorTool();
const copyShapesTool = new CopyShapesTool();

// ═══════════════════════════════════════════════════════════════════════════
// MAIN EXECUTION FUNCTION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Execute a complete buildSection operation
 * 
 * ARCHITECTURE: Reuses existing tools (DRY principle):
 * - copy → CopyShapesTool
 * - createShape → CreateOrEditShapeTool
 * - createTable → CreateOrEditTableTool
 * - createChart → CreateOrEditChartTool
 * - createConnector → CreateOrEditConnectorTool
 */
export async function executeBuildSection(
    params: BuildSectionParams,
    _context?: Partial<BuildSectionExecutionContext>
): Promise<ExecutionResult> {
    const slideId = params.slideId;
    const targetArea = params.targetArea;
    
    console.log(`[BuildSection:Execute] Starting section "${params.sectionId}" with ${params.collections.length} items`);
    console.log(`[BuildSection:Execute] Target slide: ${slideId}`);
    console.log(`[BuildSection:Execute] Using existing tools (DRY architecture)`);
    
    const createdShapes: CreatedShapeInfo[] = [];
    const warnings: string[] = [];
    const errors: string[] = [];
    
    for (let i = 0; i < params.collections.length; i++) {
        const item = params.collections[i];
        console.log(`[BuildSection:Execute] [${i + 1}/${params.collections.length}] Processing ${item.type}...`);
        
        try {
            let result: CreatedShapeInfo;
            
            switch (item.type) {
                case 'copy':
                    result = await executeCopyItem(item, slideId, targetArea);
                    break;
                    
                case 'createShape':
                    result = await executeCreateShape(item, slideId);
                    break;
                    
                case 'createTable':
                    result = await executeCreateTable(item, slideId);
                    break;
                    
                case 'createChart':
                    result = await executeCreateChart(item, slideId);
                    break;
                    
                case 'createConnector':
                    result = await executeCreateConnector(item, slideId);
                    break;
                    
                default:
                    throw new Error(`Unknown collection item type: ${(item as any).type}`);
            }
            
            createdShapes.push(result);
            if (result.warnings.length > 0) {
                warnings.push(...result.warnings);
            }
            
        } catch (itemError) {
            const errorMsg = itemError instanceof Error ? itemError.message : String(itemError);
            console.error(`[BuildSection:Execute] Error processing item ${i + 1}:`, errorMsg);
            errors.push(`Item ${i + 1} (${item.type}): ${errorMsg}`);
        }
    }
    
    console.log(`[BuildSection:Execute] Complete: ${createdShapes.length} shapes, ${warnings.length} warnings, ${errors.length} errors`);
    
    return {
        success: errors.length === 0 && createdShapes.length > 0,
        createdShapes,
        warnings,
        errors,
    };
}

// ═══════════════════════════════════════════════════════════════════════════
// SCHEMA CONVERTERS (buildSection schema → existing tool schemas)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Convert buildSection paragraphs[] to paragraphOperations[]
 * 
 * buildSection uses simple paragraphs[] array for creation.
 * createOrEditShape uses paragraphOperations[] with operation type.
 * We wrap each paragraph in an "insert" operation.
 */
function convertParagraphsToOperations(
    paragraphs?: Array<{
        setText?: string;
        text?: never;
        replaceText?: { find: string; replace: string };
        paragraphAlignment?: string;
        spaceBefore?: number;
        spaceAfter?: number;
        lineSpacing?: number;
        indentLevel?: number;
        bullet?: any;
        textFormat?: any[];
        inheritFormattingFrom?: any;
        beforeTextIndent?: number;
        hangingIndent?: number;
    }>
): Array<{
    operation: "insert";
    index: number;
    paragraph: any;
}> | undefined {
    console.log(`[BuildSection:convertParagraphs] Input paragraphs:`, JSON.stringify(paragraphs, null, 2));
    
    if (!paragraphs || paragraphs.length === 0) {
        console.log(`[BuildSection:convertParagraphs] No paragraphs to convert, returning undefined`);
        return undefined;
    }
    
    const result = paragraphs.map((para, index) => {
        const converted = {
            operation: "insert" as const,
            index,
            paragraph: {
                setText: para.setText,
                replaceText: para.replaceText,
                inheritFormattingFrom: para.inheritFormattingFrom,
                paragraphAlignment: para.paragraphAlignment,
                spaceBefore: para.spaceBefore,
                spaceAfter: para.spaceAfter,
                lineSpacing: para.lineSpacing,
                indentLevel: para.indentLevel,
                beforeTextIndent: para.beforeTextIndent,
                hangingIndent: para.hangingIndent,
                bullet: para.bullet,
                textFormat: para.textFormat,
            },
        };
        console.log(`[BuildSection:convertParagraphs] Paragraph ${index}:`, {
            input: para,
            output: converted,
        });
        return converted;
    });
    
    console.log(`[BuildSection:convertParagraphs] Converted ${result.length} paragraphs`);
    return result;
}

/**
 * Convert buildSection item to CreateOrEditShapeParams
 */
function convertToCreateOrEditShapeParams(
    item: CollectionItem,
    slideId: number
): CreateOrEditShapeParams {
    console.log(`[BuildSection:convertToShape] ========== CONVERTING ITEM ==========`);
    console.log(`[BuildSection:convertToShape] Input item:`, JSON.stringify(item, null, 2));
    console.log(`[BuildSection:convertToShape] item.textContent:`, item.textContent);
    console.log(`[BuildSection:convertToShape] item.textContent?.paragraphs:`, item.textContent?.paragraphs);
    
    // Handle image fill - resolve from context
    let fill = item.fill;
    if (fill?.type === 'image' && fill.imageSource) {
        const contextResolver = getContextResolver();
        const imageData = contextResolver.getImage(fill.imageSource);
        if (imageData) {
            // Keep as image fill - tool will handle it
            fill = {
                type: 'image',
                imageSource: fill.imageSource,
                fillMode: fill.fillMode,
            };
        }
    }
    
    // Convert paragraphs to operations
    const paragraphOperations = item.textContent?.paragraphs 
        ? convertParagraphsToOperations(item.textContent.paragraphs)
        : undefined;
    
    console.log(`[BuildSection:convertToShape] Converted paragraphOperations:`, JSON.stringify(paragraphOperations, null, 2));
    
    const result: CreateOrEditShapeParams = {
        slideId,
        operationType: 'create',
        shapeType: item.shapeType as any,
        position: item.position,
        rotation: item.rotation,
        zOrder: item.zOrder,
        fill: fill as any,
        outline: item.outline,
        shadow: item.shadow,
        textFrame: item.textFrame,
        textContent: paragraphOperations ? {
            paragraphOperations,
        } : undefined,
        metadata: item.metadata?.length ? { addEntries: item.metadata } : undefined,
    };
    
    console.log(`[BuildSection:convertToShape] Final CreateOrEditShapeParams:`, JSON.stringify(result, null, 2));
    console.log(`[BuildSection:convertToShape] ========== END CONVERSION ==========`);
    
    return result;
}

/**
 * Convert buildSection item to CreateOrEditTableParams
 */
function convertToCreateOrEditTableParams(
    item: CollectionItem,
    slideId: number
): CreateOrEditTableParams {
    if (!item.tableConfig) {
        throw new Error('tableConfig is required for createTable operation');
    }
    
    const tableConfig = item.tableConfig;
    
    // Convert cellText with paragraphs[] to paragraphOperations[]
    const cellText = tableConfig.cellText?.map(ct => ({
        cell: ct.cell,
        textFrame: ct.textFrame,
        textContent: ct.textContent?.paragraphs ? {
            paragraphOperations: convertParagraphsToOperations(ct.textContent.paragraphs),
        } : undefined,
    }));
    
    return {
        slideId,
        operationType: 'create',
        position: item.position,
        rotation: item.rotation,
        zOrder: item.zOrder,
        tableConfig: {
            rows: tableConfig.rows,
            columns: tableConfig.columns,
            columnWidths: tableConfig.columnWidths,
            rowHeights: tableConfig.rowHeights,
            mergeCells: tableConfig.mergeCells,
            cellText,
            cellFormatting: tableConfig.cellFormatting,
        },
        autoResize: true,
        metadata: item.metadata?.length ? { addEntries: item.metadata } : undefined,
    };
}

/**
 * Convert buildSection item to CreateOrEditChartParams
 */
function convertToCreateOrEditChartParams(
    item: CollectionItem,
    slideId: number
): CreateOrEditChartParams {
    if (!item.chartConfig) {
        throw new Error('chartConfig is required for createChart operation');
    }
    
    return {
        slideId,
        operationType: 'create',
        position: item.position,
        rotation: item.rotation,
        zOrder: item.zOrder,
        fill: item.fill as any,
        outline: item.outline,
        chartConfig: item.chartConfig as any,
        metadata: item.metadata?.length ? { addEntries: item.metadata } : undefined,
    };
}

/**
 * Convert buildSection item to CreateOrEditConnectorParams
 */
function convertToCreateOrEditConnectorParams(
    item: CollectionItem,
    slideId: number
): CreateOrEditConnectorParams {
    if (!item.connectorConfig) {
        throw new Error('connectorConfig is required for createConnector operation');
    }
    
    return {
        slideId,
        operationType: 'create',
        connectorConfig: item.connectorConfig as any,
        metadata: item.metadata?.length ? { addEntries: item.metadata } : undefined,
    };
}

/**
 * Convert buildSection item to CopyShapesParams
 */
function convertToCopyShapesParams(
    item: CollectionItem,
    slideId: number,
    targetArea: { left: number; top: number; width: number; height: number }
): CopyShapesParams {
    if (!item.copySource) {
        throw new Error('copySource is required for copy operation');
    }
    
    const copySource = item.copySource;
    
    // Convert shapes with text replacements
    const shapes = (copySource.shapes || []).map(s => ({
        shapeId: s.shapeId,
        targetPosition: s.targetPosition,
        textContent: s.textContent?.paragraphs ? {
            paragraphOperations: convertParagraphsToOperations(s.textContent.paragraphs),
        } : undefined,
    }));

    const normalizedTargetArea = ensureScaleMode(copySource.targetArea || targetArea);
    
    return {
        slideId,
        copySource: {
            componentId: copySource.componentId,
            slideId: copySource.slideId,
            shapes: shapes as any,
            targetArea: normalizedTargetArea,
        },
    };
}

function ensureScaleMode(
    area: { left: number; top: number; width: number; height: number; scaleMode?: "uniform" | "fitWidth" | "fitHeight" }
) {
    return {
        ...area,
        scaleMode: area.scaleMode ?? "uniform",
    };
}

// ═══════════════════════════════════════════════════════════════════════════
// EXECUTION FUNCTIONS (call existing tools)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Execute copy operation using CopyShapesTool
 */
async function executeCopyItem(
    item: CollectionItem,
    slideId: number,
    targetArea: { left: number; top: number; width: number; height: number }
): Promise<CreatedShapeInfo> {
    console.log(`[BuildSection:Copy] Using CopyShapesTool (DRY)`);
    
    const params = convertToCopyShapesParams(item, slideId, targetArea);
    const result: CopyShapesResult = await copyShapesTool.execute(params);
    
    if (!result.success) {
        throw new Error(result.errors?.join(', ') || 'Copy operation failed');
    }

    if (item.metadata?.length) {
        for (const copied of result.copiedShapes) {
            if (!copied.newShapeId) continue;
            await applyShapeMetadata({
                slideId,
                shapeId: String(copied.newShapeId),
                metadata: { addEntries: item.metadata },
            });
        }
    }
    
    // Return info for first copied shape
    const firstCopied = result.copiedShapes[0];
    return {
        type: 'copy',
        shapeId: firstCopied?.newShapeId ?? 0,
        position: firstCopied?.position ?? { left: 0, top: 0, width: 0, height: 0 },
        warnings: result.warnings || [],
    };
}

/**
 * Execute createShape using CreateOrEditShapeTool
 * 
 * This reuses the existing tool which:
 * - Tries Office.js first (preferred)
 * - Falls back to COM for unsupported features (image fills, etc.)
 */
export async function executeCreateShape(
    item: CollectionItem,
    slideId: number
): Promise<CreatedShapeInfo> {
    console.log(`[BuildSection:CreateShape] ========== EXECUTE CREATE SHAPE ==========`);
    console.log(`[BuildSection:CreateShape] Input item:`, JSON.stringify(item, null, 2));
    console.log(`[BuildSection:CreateShape] slideId: ${slideId}`);
    
    if (!item.shapeType) {
        throw new Error('shapeType is required for createShape operation');
    }
    
    const params = convertToCreateOrEditShapeParams(item, slideId);
    console.log(`[BuildSection:CreateShape] Calling createOrEditShapeTool.execute with params:`, JSON.stringify(params, null, 2));
    
    const result: CreateOrEditShapeResult = await createOrEditShapeTool.execute(params);
    console.log(`[BuildSection:CreateShape] Result:`, JSON.stringify(result, null, 2));
    
    if (!result.success) {
        throw new Error(result.errors?.join(', ') || 'Create shape failed');
    }
    
    return {
        type: 'createShape',
        shapeId: result.shapeId ?? 0,
        position: result.position ?? {
            left: item.position?.left ?? 100,
            top: item.position?.top ?? 100,
            width: item.position?.width ?? 200,
            height: item.position?.height ?? 100,
        },
        warnings: result.warnings || [],
    };
}

/**
 * Execute createTable using CreateOrEditTableTool
 * 
 * This reuses the existing tool which:
 * - Tries Office.js first (preferred)
 * - Falls back to COM for unsupported features
 */
export async function executeCreateTable(
    item: CollectionItem,
    slideId: number
): Promise<CreatedShapeInfo> {
    console.log(`[BuildSection:CreateTable] Using CreateOrEditTableTool (DRY - Office.js first, COM fallback)`);
    
    if (!item.tableConfig) {
        throw new Error('tableConfig is required for createTable operation');
    }
    
    const params = convertToCreateOrEditTableParams(item, slideId);
    const result: CreateOrEditTableResult = await createOrEditTableTool.execute(params);
    
    if (!result.success) {
        throw new Error(result.errors?.join(', ') || 'Create table failed');
    }
    
    return {
        type: 'createTable',
        shapeId: result.shapeId ?? 0,
        position: result.position ?? {
            left: item.position?.left ?? 100,
            top: item.position?.top ?? 100,
            width: item.position?.width ?? 400,
            height: item.position?.height ?? 200,
        },
        warnings: result.warnings || [],
    };
}

/**
 * Execute createChart using CreateOrEditChartTool
 * 
 * This reuses the existing tool which:
 * - Tries Office.js first (preferred)
 * - Falls back to COM for unsupported features
 */
export async function executeCreateChart(
    item: CollectionItem,
    slideId: number
): Promise<CreatedShapeInfo> {
    console.log(`[BuildSection:CreateChart] Using CreateOrEditChartTool (DRY - Office.js first, COM fallback)`);
    
    if (!item.chartConfig) {
        throw new Error('chartConfig is required for createChart operation');
    }
    
    const params = convertToCreateOrEditChartParams(item, slideId);
    const result: CreateOrEditChartResult = await createOrEditChartTool.execute(params);
    
    if (!result.success) {
        throw new Error(result.errors?.join(', ') || 'Create chart failed');
    }
    
    return {
        type: 'createChart',
        shapeId: result.shapeId ?? 0,
        position: result.position ?? {
            left: item.position?.left ?? 100,
            top: item.position?.top ?? 100,
            width: item.position?.width ?? 400,
            height: item.position?.height ?? 300,
        },
        warnings: result.warnings || [],
    };
}

/**
 * Execute createConnector using CreateOrEditConnectorTool
 * 
 * This reuses the existing tool which:
 * - Tries Office.js first (preferred)
 * - Falls back to COM for unsupported features
 */
async function executeCreateConnector(
    item: CollectionItem,
    slideId: number
): Promise<CreatedShapeInfo> {
    console.log(`[BuildSection:CreateConnector] Using CreateOrEditConnectorTool (DRY - Office.js first, COM fallback)`);
    
    if (!item.connectorConfig) {
        throw new Error('connectorConfig is required for createConnector operation');
    }
    
    const params = convertToCreateOrEditConnectorParams(item, slideId);
    const result: CreateOrEditConnectorResult = await createOrEditConnectorTool.execute(params);
    
    if (!result.success) {
        throw new Error(result.errors?.join(', ') || 'Create connector failed');
    }
    
    // Calculate bounds from points
    const points = item.connectorConfig.points;
    const minX = Math.min(...points.map(p => p.x));
    const maxX = Math.max(...points.map(p => p.x));
    const minY = Math.min(...points.map(p => p.y));
    const maxY = Math.max(...points.map(p => p.y));
    
    return {
        type: 'createConnector',
        shapeId: result.shapeId ?? 0,
        position: {
            left: minX,
            top: minY,
            width: maxX - minX,
            height: maxY - minY,
        },
        warnings: result.warnings || [],
    };
}

// ═══════════════════════════════════════════════════════════════════════════
// COPY SHAPES (EXPORTED FOR DIRECT USE)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Copy shapes between slides - delegates to CopyShapesTool
 * Exported for direct use by agents
 */
export async function executeCopyShapes(
    params: {
        documentId: string;
        targetSlideId: number;
        sourceSlideId?: number;
        sourceComponentId?: string;
        shapes: Array<{
            shapeId: number;
            targetPosition?: {
                left?: number;
                top?: number;
                width?: number;
                height?: number;
            };
            textReplacements?: Array<{
                find: string;
                replace: string;
            }>;
        }>;
        targetArea?: {
            left: number;
            top: number;
            width: number;
            height: number;
        };
    }
): Promise<{
    success: boolean;
    copiedShapes: Array<{
        sourceShapeId: number;
        newShapeId: number;
        position: { left: number; top: number; width: number; height: number };
    }>;
    warnings: string[];
    errors: string[];
}> {
    // Convert to CopyShapesParams and use the tool
    const copyParams: CopyShapesParams = {
        slideId: params.targetSlideId,
        copySource: {
            componentId: params.sourceComponentId,
            slideId: params.sourceSlideId,
            shapes: params.shapes.map(s => ({
                shapeId: s.shapeId,
                targetPosition: s.targetPosition,
                // Convert textReplacements to textContent format (CopyShapes uses paragraphs[], not paragraphOperations[])
                textContent: s.textReplacements && s.textReplacements.length > 0 ? {
                    paragraphs: s.textReplacements.map(tr => ({
                        replaceText: tr,
                    })),
                } : undefined,
            })),
            targetArea: ensureScaleMode(params.targetArea),
        },
    };
    
    const result = await copyShapesTool.execute(copyParams);
    
    return {
        success: result.success,
        copiedShapes: result.copiedShapes,
        warnings: result.warnings,
        errors: result.errors,
    };
}
